/* ==========================================================================
   Initiative Time — behaviour
   Custom elements, no framework, no build step. Everything degrades to a
   working page if JavaScript fails: forms post normally, links navigate.
   ========================================================================== */

(function () {
  "use strict";

  const routes = window.ITRoutes || {};
  const cartUrl = routes.cart || "/cart";

  /* --- helpers ---------------------------------------------------------- */

  function money(cents) {
    const format = window.ITMoneyFormat || "£{{amount}}";
    const amount = (cents / 100).toFixed(2);
    const parts = amount.split(".");
    const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return format
      .replace(/\{\{\s*amount\s*\}\}/, withCommas + "." + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withCommas + "," + parts[1]);
  }

  async function refreshCartCount() {
    try {
      const res = await fetch(`${cartUrl}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return;
      const cart = await res.json();
      document.querySelectorAll("[data-cart-count]").forEach((el) => {
        el.textContent = cart.item_count;
      });
      document.dispatchEvent(
        new CustomEvent("cart:count", { detail: { count: cart.item_count } })
      );
    } catch (e) {
      /* count is cosmetic; a failure here should not break the page */
    }
  }

  function replaceSection(html, selector) {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const incoming = parsed.querySelector(selector);
    const current = document.querySelector(selector);
    if (incoming && current) {
      current.innerHTML = incoming.innerHTML;
      return true;
    }
    return false;
  }

  function trapFocus(container) {
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    function onKey(e) {
      if (e.key !== "Tab") return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener("keydown", onKey);
    first.focus();
    return () => container.removeEventListener("keydown", onKey);
  }

  /* --- cart drawer ------------------------------------------------------ */

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.releaseFocus = null;
      this.addEventListener("click", (e) => {
        if (
          e.target.closest("[data-cart-close]") ||
          e.target.classList.contains("cart-drawer__scrim")
        ) {
          this.close();
        }
      });
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.hasAttribute("open")) this.close();
      });
      document.addEventListener("cart:open", () => this.open());
      document.addEventListener("cart:refresh", (e) => this.refresh(e.detail));
    }

    open() {
      this.setAttribute("open", "");
      document.body.style.overflow = "hidden";
      const panel = this.querySelector(".cart-drawer__panel");
      if (panel) this.releaseFocus = trapFocus(panel);
    }

    close() {
      this.removeAttribute("open");
      document.body.style.overflow = "";
      if (this.releaseFocus) this.releaseFocus();
      const opener = document.querySelector("[data-cart-toggle]");
      if (opener) opener.focus();
    }

    refresh(detail) {
      if (detail && detail.html) {
        replaceSection(detail.html, "#cart-drawer-section");
      }
      refreshCartCount();
    }
  }
  customElements.define("cart-drawer", CartDrawer);

  /* --- menu drawer ------------------------------------------------------ */

  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.toggle = document.querySelector("[data-menu-toggle]");
      if (this.toggle) {
        this.toggle.addEventListener("click", () => this.open());
      }
      this.addEventListener("click", (e) => {
        if (
          e.target.closest("[data-menu-close]") ||
          e.target.classList.contains("menu-drawer__scrim")
        ) {
          this.close();
        }
      });
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.hasAttribute("open")) this.close();
      });
    }

    open() {
      this.setAttribute("open", "");
      document.body.style.overflow = "hidden";
      const panel = this.querySelector(".menu-drawer__panel");
      if (panel) this.releaseFocus = trapFocus(panel);
      if (this.toggle) this.toggle.setAttribute("aria-expanded", "true");
    }

    close() {
      this.removeAttribute("open");
      document.body.style.overflow = "";
      if (this.releaseFocus) this.releaseFocus();
      if (this.toggle) {
        this.toggle.setAttribute("aria-expanded", "false");
        this.toggle.focus();
      }
    }
  }
  customElements.define("menu-drawer", MenuDrawer);

  /* --- quantity stepper ------------------------------------------------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector("input");
      if (!this.input) return;
      this.querySelectorAll("button").forEach((button) => {
        button.addEventListener("click", (e) => {
          e.preventDefault();
          const step = button.dataset.step === "up" ? 1 : -1;
          const min = parseInt(this.input.min || "1", 10);
          const next = Math.max(min, (parseInt(this.input.value, 10) || min) + step);
          this.input.value = next;
          this.input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      });
    }
  }
  customElements.define("quantity-input", QuantityInput);

  /* --- variant picker --------------------------------------------------- */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('script[type="application/json"]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.sectionId = this.dataset.section;
      this.addEventListener("change", () => this.onChange());
      this.updateAvailability();
    }

    get selectedOptions() {
      return Array.from(
        this.querySelectorAll('input[type="radio"]:checked')
      ).map((input) => input.value);
    }

    findVariant(options) {
      return this.variants.find((variant) =>
        variant.options.every((value, index) => value === options[index])
      );
    }

    onChange() {
      const variant = this.findVariant(this.selectedOptions);
      this.updateAvailability();
      if (!variant) {
        this.setUnavailable();
        return;
      }
      this.updateUrl(variant);
      this.updateInput(variant);
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateGallery(variant);
      document.querySelectorAll("[data-selected-option]").forEach((el) => {
        const index = parseInt(el.dataset.selectedOption, 10);
        el.textContent = variant.options[index] || "";
      });
    }

    /* Grey out sizes that do not exist in the chosen colour. */
    updateAvailability() {
      const chosen = this.selectedOptions;
      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        const index = parseInt(input.dataset.optionIndex, 10);
        if (Number.isNaN(index) || index === 0) return;
        const probe = chosen.slice();
        probe[index] = input.value;
        const match = this.variants.find((variant) =>
          variant.options.every((value, i) => {
            if (i > index) return true;
            return value === probe[i];
          })
        );
        input.disabled = !match || !match.available;
      });
    }

    setUnavailable() {
      const button = document.querySelector(`[data-add-button]`);
      if (button) {
        button.setAttribute("aria-disabled", "true");
        button.textContent = button.dataset.unavailable || "Unavailable";
      }
    }

    updateUrl(variant) {
      if (!variant || this.dataset.updateUrl === "false") return;
      window.history.replaceState({}, "", `${this.dataset.url}?variant=${variant.id}`);
    }

    updateInput(variant) {
      const input = document.querySelector("[data-variant-id]");
      if (input) {
        input.value = variant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    updatePrice(variant) {
      const amount = document.querySelector("[data-price-amount]");
      const compare = document.querySelector("[data-price-compare]");
      if (amount) amount.textContent = money(variant.price);
      if (compare) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compare.textContent = money(variant.compare_at_price);
          compare.hidden = false;
        } else {
          compare.hidden = true;
        }
      }
    }

    updateButton(variant) {
      const button = document.querySelector("[data-add-button]");
      if (!button) return;
      if (variant.available) {
        button.removeAttribute("aria-disabled");
        button.textContent = button.dataset.addLabel || "Add to basket";
      } else {
        button.setAttribute("aria-disabled", "true");
        button.textContent = button.dataset.soldOut || "Sold out";
      }
    }

    updateGallery(variant) {
      if (!variant.featured_media) return;
      const main = document.querySelector("[data-gallery-main] img");
      if (main && variant.featured_media.src) {
        main.src = variant.featured_media.src;
        main.srcset = "";
        main.alt = variant.featured_media.alt || main.alt;
      }
      document.querySelectorAll("[data-gallery-thumb]").forEach((thumb) => {
        thumb.setAttribute(
          "aria-current",
          String(thumb.dataset.mediaId === String(variant.featured_media.id))
        );
      });
    }
  }
  customElements.define("variant-picker", VariantPicker);

  /* --- product gallery -------------------------------------------------- */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector("[data-gallery-main] img");
      this.querySelectorAll("[data-gallery-thumb]").forEach((thumb) => {
        thumb.addEventListener("click", () => {
          if (!this.main) return;
          this.main.src = thumb.dataset.full;
          this.main.srcset = "";
          this.main.alt = thumb.querySelector("img")
            ? thumb.querySelector("img").alt
            : "";
          this.querySelectorAll("[data-gallery-thumb]").forEach((t) =>
            t.setAttribute("aria-current", String(t === thumb))
          );
        });
      });
    }
  }
  customElements.define("product-gallery", ProductGallery);

  /* --- add to cart ------------------------------------------------------ */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector("form");
      if (!this.form) return;
      this.button = this.querySelector("[data-add-button]");
      this.errorEl = this.querySelector("[data-form-error]");
      this.form.addEventListener("submit", (e) => this.onSubmit(e));
    }

    async onSubmit(e) {
      if (this.button && this.button.getAttribute("aria-disabled") === "true") {
        e.preventDefault();
        return;
      }
      if (window.ITCartBehaviour === "page") return; /* let it post normally */
      e.preventDefault();

      const formData = new FormData(this.form);
      formData.append("sections", "cart-drawer");
      formData.append("sections_url", window.location.pathname);

      this.setBusy(true);
      if (this.errorEl) this.errorEl.textContent = "";

      try {
        const res = await fetch(`${routes.cart_add || "/cart/add"}.js`, {
          method: "POST",
          headers: { Accept: "application/javascript" },
          body: formData,
        });
        const data = await res.json();

        if (!res.ok || data.status) {
          if (this.errorEl) {
            this.errorEl.textContent =
              data.description || data.message || "Something went wrong.";
          }
          return;
        }

        const html =
          data.sections && data.sections["cart-drawer"]
            ? data.sections["cart-drawer"]
            : null;
        if (html) replaceSection(html, "#cart-drawer-section");
        await refreshCartCount();
        document.dispatchEvent(new CustomEvent("cart:open"));
      } catch (err) {
        if (this.errorEl)
          this.errorEl.textContent = "Could not reach the basket. Please try again.";
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(state) {
      if (!this.button) return;
      this.button.setAttribute("aria-busy", String(state));
      if (state) {
        this.button.dataset.restore = this.button.textContent;
        this.button.textContent = this.button.dataset.adding || "Adding…";
      } else if (this.button.dataset.restore) {
        this.button.textContent = this.button.dataset.restore;
      }
    }
  }
  customElements.define("product-form", ProductForm);

  /* --- cart line changes ------------------------------------------------ */

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener("click", (e) => {
        const remove = e.target.closest("[data-line-remove]");
        if (remove) {
          e.preventDefault();
          this.change(remove.dataset.line, 0);
        }
      });
      this.addEventListener("change", (e) => {
        const input = e.target.closest("[data-line-qty]");
        if (input) this.change(input.dataset.line, input.value);
      });
    }

    async change(line, quantity) {
      this.setAttribute("aria-busy", "true");
      try {
        const res = await fetch(`${cartUrl}/change.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/javascript",
          },
          body: JSON.stringify({
            line: Number(line),
            quantity: Number(quantity),
            sections: "cart-drawer",
          }),
        });
        const data = await res.json();
        if (data.sections && data.sections["cart-drawer"]) {
          replaceSection(data.sections["cart-drawer"], "#cart-drawer-section");
        }
        await refreshCartCount();
        if (document.body.classList.contains("template-cart")) {
          window.location.reload();
        }
      } catch (err) {
        window.location.href = cartUrl;
      } finally {
        this.removeAttribute("aria-busy");
      }
    }
  }
  customElements.define("cart-items", CartItems);

  /* --- open the drawer from the header --------------------------------- */

  document.addEventListener("click", (e) => {
    const toggle = e.target.closest("[data-cart-toggle]");
    if (!toggle) return;
    if (window.ITCartBehaviour === "page") return;
    e.preventDefault();
    document.dispatchEvent(new CustomEvent("cart:open"));
  });

  /* --- sort select ------------------------------------------------------ */

  document.addEventListener("change", (e) => {
    const select = e.target.closest("[data-sort-select]");
    if (select) select.form.submit();
  });
})();
